<script setup lang="ts">
import { overlaySpinnerService } from '@/services/app/overlay-spinner-service.ts';
import { notificationService } from '@/services/notification/notification-service';
import { outlookInboxService } from '@/services/outlook-inbox/outlook-inbox-service';
import type { InboxDocument } from 'cesg-shared/dist/models';
import { PhTrash } from '@phosphor-icons/vue';
import { computed, onActivated, onMounted, ref } from 'vue';

import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { RouteName } from '@/router/models/route-name';

const { t } = useI18n();
const router = useRouter();

const inboxDocuments = ref<InboxDocument[]>([]);
const selectedInboxDocument = ref<InboxDocument | null>(null);

const searchQuery = ref<string>('');
const inboxSortKey = ref<InboxSortKey>('receivedAt');
const sortOrder = ref<SortOrder>('none');
const showDeleteModal = ref(false);

type SortOrder = 'none' | 'asc' | 'desc';
type InboxSortKey = keyof InboxDocument;

const promptDelete = (doc: InboxDocument) => {
  selectedInboxDocument.value = doc;
  showDeleteModal.value = true;
};

const confirmDelete = async () => {
  showDeleteModal.value = false;
  if (!selectedInboxDocument.value) return;

  overlaySpinnerService.show();

  try {
    outlookInboxService.deleteInboxDocument(selectedInboxDocument.value);
    selectedInboxDocument.value = null;
    manualInboxSync();
  } catch (error) {
    console.error('Delete error', error);

    notificationService.open({
      text: t('adminSupplierInvoice.modalWarning.removeUnsuccessful'),
      type: 'danger',
    });
  } finally {
    overlaySpinnerService.hide();
  }
};

const cancelDelete = () => {
  selectedInboxDocument.value = null;
  showDeleteModal.value = false;
};

const compareInboxDocuments = (
  a: InboxDocument,
  b: InboxDocument,
  key: keyof InboxDocument,
  order: SortOrder,
): number => {
  let aValue: string | number;
  let bValue: string | number;

  switch (key) {
    case 'receivedAt':
      aValue = new Date(a[key]).getTime();
      bValue = new Date(b[key]).getTime();
      break;
    default:
      aValue = String(a[key]).toLowerCase();
      bValue = String(b[key]).toLowerCase();
      break;
  }

  let comparison = 0;
  if (aValue > bValue) {
    comparison = 1;
  } else if (aValue < bValue) {
    comparison = -1;
  }

  return order === 'desc' ? comparison * -1 : comparison;
};

// watch(activeFilter, (value: FilterStatus) => {
//   if (value === 'incoming') manualInboxSync();
// });

// NOTE: Temporary manual load of inbox data until put into scheduler
const manualInboxSync = async () => {
  overlaySpinnerService.show();
  try {
    await outlookInboxService.syncOutlookInbox();
    inboxDocuments.value =
      await outlookInboxService.getAllSyncedInboxDocuments();
  } catch (error) {
    console.error(t('adminSupplierInvoice.failedToSyncInvoiceInbox'), error);
  } finally {
    overlaySpinnerService.hide();
  }
};

const navigateCreateFromInboxDocument = (doc: InboxDocument) => {
  router.push({
    name: RouteName.AdminCreateInvoices,
    query: { inboxDocumentId: doc.id },
  });
};

const sortedInboxDocuments = computed(() => {
  let result = inboxDocuments.value;

  const query = searchQuery.value?.toLowerCase().trim();
  if (query) {
    result = result.filter((inboxDocument) => {
      return (
        fmtDate(inboxDocument.receivedAt).toLowerCase().includes(query) ||
        inboxDocument.sender.toLowerCase().includes(query) ||
        inboxDocument.subject?.toLowerCase().includes(query) ||
        inboxDocument.filename.toLowerCase().includes(query)
      );
    });
  }

  if (inboxSortKey.value) {
    result = [...result].sort((a, b) =>
      compareInboxDocuments(a, b, inboxSortKey.value, sortOrder.value),
    );
  }

  return result;
});

const toggleSortOrder = (currentOrder: SortOrder): SortOrder => {
  if (currentOrder === 'asc') return 'desc';
  if (currentOrder === 'desc') return 'none';
  return 'asc';
};

const toggleInboxDocumentSortState = (key: keyof InboxDocument) => {
  if (inboxSortKey.value === key) {
    sortOrder.value = toggleSortOrder(sortOrder.value);
  } else {
    inboxSortKey.value = key;
    sortOrder.value = 'asc';
  }

  if (sortOrder.value === 'none') {
    inboxSortKey.value = 'receivedAt';
  }
};

const getSortIndicator = (order: SortOrder) => {
  if (order === 'asc') return '▲';
  if (order === 'desc') return '▼';
  return '';
};

onMounted(() => {
  manualInboxSync();
});

onActivated(async () => {
  await manualInboxSync();
});

const fmtDate = (isoString: string) => {
  if (!isoString) return '-';
  return new Date(isoString).toLocaleString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fmtSize = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};
</script>

<template>
  <div class="invoice-table-wrapper">
    <div class="invoice-table">
      <table>
        <thead>
          <tr>
            <th
              class="sortable"
              @click="toggleInboxDocumentSortState('receivedAt')"
              :class="{ active: inboxSortKey === 'receivedAt' }"
            >
              {{ t('adminSupplierInvoice.tableHeaders.Received') }}
              <span v-if="inboxSortKey === 'receivedAt'" class="sort-indicator">
                {{ getSortIndicator(sortOrder) }}
              </span>
            </th>
            <th
              class="sortable"
              @click="toggleInboxDocumentSortState('sender')"
              :class="{ active: inboxSortKey === 'sender' }"
            >
              {{ t('adminSupplierInvoice.tableHeaders.Sender') }}
              <span v-if="inboxSortKey === 'sender'" class="sort-indicator">
                {{ getSortIndicator(sortOrder) }}
              </span>
            </th>
            <th
              class="sortable"
              @click="toggleInboxDocumentSortState('subject')"
              :class="{ active: inboxSortKey === 'subject' }"
            >
              {{ t('adminSupplierInvoice.tableHeaders.Subject') }}
              <span v-if="inboxSortKey === 'subject'" class="sort-indicator">
                {{ getSortIndicator(sortOrder) }}
              </span>
            </th>
            <th
              class="sortable"
              @click="toggleInboxDocumentSortState('filename')"
              :class="{ active: inboxSortKey === 'filename' }"
            >
              {{ t('adminSupplierInvoice.tableHeaders.Filename') }}
              <span v-if="inboxSortKey === 'filename'" class="sort-indicator">
                {{ getSortIndicator(sortOrder) }}
              </span>
            </th>
            <th
              class="sortable"
              @click="toggleInboxDocumentSortState('size')"
              :class="{ active: inboxSortKey === 'size' }"
            >
              {{ t('adminSupplierInvoice.tableHeaders.Size') }}
              <span v-if="inboxSortKey === 'size'" class="sort-indicator">
                {{ getSortIndicator(sortOrder) }}
              </span>
            </th>
            <th>
              <button
                class="btn-bordered btn-sync-invoice-documents"
                @click="manualInboxSync"
              >
                {{ t('adminSupplierInvoice.syncInbox') }}
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="doc in sortedInboxDocuments"
            :key="doc.id"
            @click="navigateCreateFromInboxDocument(doc)"
          >
            <td>{{ fmtDate(doc.receivedAt) }}</td>
            <td>{{ doc.sender }}</td>
            <td>{{ doc.subject }}</td>
            <td>{{ doc.filename }}</td>
            <td>{{ fmtSize(doc.size) }}</td>
            <td>
              <button
                class="btn-delete"
                title="Ta bort"
                @click.stop="promptDelete(doc)"
              >
                <PhTrash size="22" weight="bold" style="margin-right: 8px" />
                {{ t('adminSupplierInvoice.modalWarning.button.delete') }}
              </button>
            </td>
          </tr>
          <tr v-if="inboxDocuments.length === 0">
            <td
              colspan="6"
              style="text-align: center; padding: 20px; color: #666"
            >
              {{ t('adminSupplierInvoice.noDataDescription') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <teleport to="body">
    <div v-if="showDeleteModal && selectedInboxDocument" class="modal-overlay">
      <div class="modal-box">
        <h3 class="modal-title">
          {{ t('adminSupplierInvoice.modalWarning.deleteDocument') }}
        </h3>
        <table>
          <thead>
            <tr>
              <th>{{ t('adminSupplierInvoice.tableHeaders.Received') }}</th>
              <th>{{ t('adminSupplierInvoice.tableHeaders.Sender') }}</th>
              <th>{{ t('adminSupplierInvoice.tableHeaders.Subject') }}</th>
              <th>{{ t('adminSupplierInvoice.tableHeaders.Filename') }}</th>
              <th>{{ t('adminSupplierInvoice.tableHeaders.Size') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ fmtDate(selectedInboxDocument.receivedAt) }}</td>
              <td>{{ selectedInboxDocument.sender }}</td>
              <td>{{ selectedInboxDocument.subject }}</td>
              <td>{{ selectedInboxDocument.filename }}</td>
              <td>{{ fmtSize(selectedInboxDocument.size) }}</td>
            </tr>
          </tbody>
        </table>
        <p class="modal-hint" style="font-weight: bold; color: #b33939">
          {{ t('adminSupplierInvoice.modalWarning.actionWarning') }}
        </p>

        <div class="modal-actions">
          <button
            type="button"
            class="btn-modal-secondary"
            @click="cancelDelete"
          >
            {{ t('adminSupplierInvoice.modalWarning.button.cancel') }}
          </button>
          <button type="button" class="btn-modal-danger" @click="confirmDelete">
            {{ t('adminSupplierInvoice.modalWarning.button.confirm') }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<style scoped lang="scss">
@use '../../../../node_modules/bulma/sass/utilities/initial-variables.scss' as *;
@use '@/assets/style/popup';

.invoice-table-wrapper {
  .top-row-controls {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-bottom: 16px;
    gap: 16px;

    @media screen and (max-width: $desktop) {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .search-field {
    .input.search {
      width: 300px;
      padding: 8px 12px;
      border: 1px solid #ccc;
      border-radius: 4px;

      @media screen and (max-width: $desktop) {
        width: 100%;
        margin-bottom: 16px;
      }
    }
  }

  .btn-bordered {
    display: inline-block;
    flex-shrink: 0;
    box-sizing: border-box;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    background-color: #cbf8de;
    color: black;
    border: 1px solid #ccc;
    text-decoration: none;
    text-align: center;
    line-height: normal;
    font-size: 14px;
    font-weight: 500;

    &:hover {
      opacity: 0.9;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
      color: black;
      text-decoration: none;
      border-color: #bcebcf;
    }

    &:active {
      box-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.125);
    }
  }

  .btn-sync-invoice-documents {
    margin-left: auto;
    background-color: #e3f2fd;
    border-color: #90caf9;
  }

  .btn-delete {
    display: flex;
    align-items: center;
    padding: 6px 12px;
    border: 1px solid #ccc;
    cursor: pointer;
    border-radius: 4px;

    background-color: #fdeaea;
    color: #b33939;
  }

  .btn-delete:hover {
    background-color: #f3c9c9;
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);
  }

  .button-row {
    display: flex;
    gap: 8px;

    @media screen and (max-width: $desktop) {
      width: 100%;
      overflow-x: auto;
      padding-bottom: 8px;
    }

    button {
      flex-shrink: 0;
      padding: 6px 12px;
      border: 1px solid #ccc;
      cursor: pointer;
      border-radius: 4px;
      transition: all 0.2s ease-in-out;

      &.active {
        border-color: #007bff;
        font-weight: bold;
        box-shadow: 0 2px 8px rgba(0, 123, 255, 0.4);
      }

      &:hover {
        opacity: 0.9;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
      }
    }
  }

  .invoice-table {
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    overflow-x: auto;

    table {
      width: 100%;
      min-width: 800px;
      border-collapse: collapse;

      th,
      td {
        padding: 7px;
        text-align: left;
        border: none;
        border-bottom: 1px solid #eee;
        font-size: 0.9em;
      }

      thead {
        background-color: white;

        th {
          font-weight: 600;
          box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.05);
          border-bottom: 2px solid #ddd;
          position: relative;
          user-select: none;

          &.sortable {
            cursor: pointer;

            &:hover {
              background-color: rgba(0, 0, 0, 0.03);
            }
          }

          .sort-indicator {
            position: absolute;
            right: 5px;
            top: 50%;
            transform: translateY(-50%);
            font-size: 0.8em;
            color: #555;
          }
        }

        th:first-child {
          border-top-left-radius: 8px;
        }

        th:last-child {
          display: flex;
          border-top-right-radius: 8px;
          justify-items: flex-end;
        }
      }

      tbody {
        tr {
          transition: background-color 0.15s ease;

          td {
            align-content: center;

            &:last-child {
              justify-items: right;
            }
          }

          &:hover {
            background-color: rgba(0, 0, 0, 0.03);
            cursor: pointer;
          }

          &:last-child td {
            border-bottom: none;
          }
        }

        tr.incoming:hover td,
        tr.undercertificate:hover td,
        tr.parked:hover td,
        tr.disputed:hover td,
        tr.btn-voided:hover td {
          box-shadow: inset 0 0 0 1000px rgba(0, 0, 0, 0.04);
        }

        tr.incoming {
          background-color: #f4f5f6;
        }

        tr.undercertificate {
          background-color: #cbdef8;
        }

        tr.parked {
          background-color: #f8decb;
        }

        tr.disputed {
          background-color: #f8cbcb;
        }

        tr.voided {
          background-color: #ef9a9a;
        }

        tr:last-child td:first-child {
          border-bottom-left-radius: 8px;
        }

        tr:last-child td:last-child {
          border-bottom-right-radius: 8px;
        }
      }
    }
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.modal-box {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: white;
  padding: 1.4rem;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  text-align: center;
  border: 1px solid #e2e8f0;
  animation: modalFadeIn 0.2s ease-out;
  min-width: 50% !important;
  max-width: 80% !important;
  min-height: 10% !important;
  max-height: 50% !important;

  table {
    background-color: white;
    border-collapse: separate;
    justify-self: center;

    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);

    thead {
      tr {
        th {
          border-bottom: 2px solid #ddd;
          text-align: left;
          padding: 0.75rem;
          font-weight: 600;
        }
      }
    }

    tbody {
      background-color: #f4f5f6;

      tr {
        td {
          text-align: left;
          padding: 0.75rem;
          text-align: left;
          border: none;
          font-size: 0.9em;
        }
      }
    }
  }
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal-title {
  display: flex;
  justify-content: flex-start;
  width: 100%;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.modal-hint {
  display: flex;
  justify-content: flex-start;
  width: 100%;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1.5rem;
}

.modal-text {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  color: #64748b;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.modal-actions {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.btn-modal-secondary,
.btn-modal-danger {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-modal-secondary {
  background-color: #f1f5f9;
  color: #475569;

  &:hover {
    background-color: #e2e8f0;
    color: #1e293b;
  }
}

.btn-modal-danger {
  background-color: #ef4444;
  color: white;

  &:hover {
    background-color: #dc2626;
  }
}
</style>
